/**
 * Báo giá NCC dạng bảng tính (phần D). Hàng 1 là vai trò cột, hàng 2 là tiêu đề,
 * dữ liệu từ hàng 3. Người mua tự gõ công thức như Excel; máy chủ tính lại để đối chiếu.
 */
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom'
import { Workbook } from '@fortune-sheet/react'
import type { WorkbookInstance } from '@fortune-sheet/react'
import '@fortune-sheet/react/dist/index.css'
import type { Sheet } from '@fortune-sheet/core'
import { PO_OFF_BG, PO_OFF_FC, PO_ON_BG, PO_ON_FC } from './quoteSheetModel'
import { Button } from '@/components/ui/Button'
import { Typeahead } from '@/components/forms/Typeahead'
import {
  borderInfoFor,
  celldataFromGrid,
  columnLetter,
  columnsMissingRole,
  gridFromFortuneMatrix,
  layoutFor,
  describeRowClassification,
  guessRoleFromLabel,
  parseAddress,
  planFillFormula,
  poVisibleByHeader,
  poVisibleForGrid,
  poEyeStateForColumn,
  togglePoVisibleColumn,
  isPoEyeHit,
  quoteSheetCellText,
  normalizeQuoteSheetUnit,
  normalizeQuoteSheetUnits,
  normalizeGridInput,
  normalizePoVisibleInput,
  QUOTE_SHEET_UOM_OPTIONS,
  reindexQuoteSheetRows,
  readRoleRow,
  SHEET_HEADER_ROWS,
  SHEET_ROLE_LABEL,
  normalizeItemName,
  suggestItemIdsByName,
  trimGrid,
  type SheetGrid,
} from '@/features/suppliers/quoteSheetModel'
import { pendingFormulaCells, shouldMaskPendingFormulaCell, templateGrid, unlistedPoColumnHints } from '@/features/suppliers/quoteSheetDisplay'
import {
  calculateQuoteSheet,
  getQuoteSheet,
  previewQuoteSheetPublish,
  publishQuoteSheet,
  saveQuoteSheet,
  type PublishPreview,
  type QuoteSheetCalc,
} from '@/services/suppliers/quoteSheetService'
import { materialService, type Material } from '@/services/materials'
import { normalizeUomLabel } from '@/lib/uom'
import {
  applyQuoteSheetTemplate,
  listQuoteSheetTemplates,
  saveQuoteSheetTemplate,
  deleteQuoteSheetTemplate,
  type QuoteSheetTemplate,
} from '@/services/suppliers/quoteSheetService'
import './QuoteSheetPanel.css'
import { formatSheetAmount } from '@/lib/sheetAmount'

const STARTER_HEADERS = ['Tên hàng', 'KT1', 'KT2', 'KT3', 'KT4', 'Đơn giá', 'Giá', 'Công thức', 'Ghi chú']

/** Lưới mẫu cho NCC chưa có bản nháp: hàng vai trò đoán theo tiêu đề, hàng tiêu đề, chưa có dữ liệu. */
function starterGrid(): SheetGrid {
  const roles = STARTER_HEADERS.map((h) => {
    const role = guessRoleFromLabel(h)
    return role ? SHEET_ROLE_LABEL[role] : null
  })
  return [roles, STARTER_HEADERS]
}

const ZOOM_MIN = 0.5
const ZOOM_MAX = 2.5

function clampZoom(z: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100))
}

export interface CellPosition {
  row: number
  col: number
}

export interface TabNavigationOptions {
  totalRows?: number
  totalCols?: number
  shiftKey?: boolean
}

/**
 * Tính vị trí ô tiếp theo khi nhấn Tab hoặc Shift+Tab.
 * Tab sang phải (c + 1); Shift+Tab sang trái (c - 1).
 * Ở cột cuối: giữ nguyên cột cuối (hoặc cột 0 nếu Shift+Tab ở đầu bảng).
 */
export function nextCellOnTab(
  current: CellPosition,
  options: TabNavigationOptions = {},
): CellPosition {
  const totalCols = options.totalCols ?? 20
  const { row, col } = current
  if (options.shiftKey) {
    return { row, col: Math.max(0, col - 1) }
  }
  return { row, col: Math.min(Math.max(0, totalCols - 1), col + 1) }
}

function sheetsFromGrid(grid: SheetGrid, zoom = 1, poVisible: boolean[] = []): Sheet[] {
  const width = Math.max(0, ...grid.map((row) => row.length))
  const rows = Math.max(grid.length + 30, 40)
  const roles = grid[0] ?? []
  const headers = grid[1] ?? []
  const unitColumns = headers.flatMap((header, col) => {
    const label = typeof header === 'string' ? header.trim().toLocaleLowerCase('vi') : ''
    return roles[col] === SHEET_ROLE_LABEL.uom || label === 'đv đơn giá' ? [col] : []
  })
  const dataVerification: Record<string, unknown> = {}
  for (let row = 2; row < rows; row++) {
    for (const column of unitColumns) {
      dataVerification[`${row}_${column}`] = {
        type: 'dropdown', value1: QUOTE_SHEET_UOM_OPTIONS.join(','), type2: 'false',
        hintShow: true, hintText: `Chọn một đơn vị: ${QUOTE_SHEET_UOM_OPTIONS.join(', ')}`,
      }
    }
  }
  return [
    {
      name: 'Báo giá NCC',
      order: 0,
      status: 1,
      row: rows,
      column: Math.max(width + 6, 14),
      celldata: celldataFromGrid(grid, poVisible),
      zoomRatio: zoom,
      frozen: { type: 'rangeRow', range: { row_focus: SHEET_HEADER_ROWS - 1, column_focus: 0 } },
      config: { borderInfo: borderInfoFor(grid), ...layoutFor(grid) },
      dataVerification,
      luckysheet_select_save: [
        {
          row: [0, 0],
          column: [0, 0],
          row_focus: 0,
          column_focus: 0,
          left: 0,
          width: 73,
          top: 0,
          height: 19,
          left_move: 0,
          width_move: 73,
          top_move: 0,
          height_move: 19,
        },
      ],
    } as unknown as Sheet,
  ]
}

/** So giá trị máy chủ tính với giá trị FortuneSheet đang hiển thị. */
function sameValue(serverValue: string | boolean | null, sheetValue: unknown): boolean {
  if (serverValue === null) return sheetValue === null || sheetValue === undefined || sheetValue === ''
  if (typeof serverValue === 'boolean') return sheetValue === serverValue
  const a = Number(serverValue)
  const b = typeof sheetValue === 'number' ? sheetValue : Number(sheetValue)
  if (Number.isFinite(a) && Number.isFinite(b)) return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a))
  return String(sheetValue ?? '') === serverValue
}

/**
 * Chặn chuyển trang khi đang sửa có thay đổi chưa lưu. Chỉ hoạt động trong data router;
 * ngoài router (ví dụ SSR) thì không làm gì.
 */
function EditNavigationGuard({ active }: { active: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  if (!dataRouter) return null
  return <EditNavigationGuardInner active={active} />
}

function EditNavigationGuardInner({ active }: { active: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      active && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    const ok = window.confirm('Bạn đang sửa báo giá và có thay đổi chưa lưu. Rời đi sẽ mất các thay đổi này?')
    if (ok) blocker.proceed()
    else blocker.reset()
  }, [blocker])
  return null
}

export function QuoteSheetPanel({ supplierId }: { supplierId: string }) {
  const wbRef = useRef<WorkbookInstance | null>(null)
  const [initial, setInitial] = useState<{ key: number; grid: SheetGrid; sheets: Sheet[] } | null>(null)
  /** Mức zoom xem bảng (Ctrl + lăn chuột), chỉ áp dụng ở chế độ xem. */
  const [zoom, setZoom] = useState(1)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [calc, setCalc] = useState<(QuoteSheetCalc & { mismatches: string[] }) | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  /** Đơn vị mặc định: ghi đúng như dữ liệu, "Pcs" là giá trị khởi tạo (ô cho người dùng sửa). */
  const [uomDefault, setUomDefault] = useState('Pcs')
  const [materials, setMaterials] = useState<Material[]>([])
  /** Vật tư gắn cho từng dòng xem trước, theo số dòng trên lưới. */
  const [itemIds, setItemIds] = useState<Record<string, string | null>>({})
  const [preview, setPreview] = useState<PublishPreview | null>(null)
  const [publishing, setPublishing] = useState(false)
  /** Khóa mặc định; chỉ sửa được sau khi bấm "Sửa". */
  const [editing, setEditing] = useState(false)
  /** Hàng 1 không còn nhãn vai trò hợp lệ (thường do người dùng xóa hàng này). */
  const [roleRowMissing, setRoleRowMissing] = useState(false)
  const [templates, setTemplates] = useState<QuoteSheetTemplate[]>([])
  /** Cờ hiện trên PO theo tên tiêu đề; không lệch khi người dùng chèn/xóa cột. */
  const [poVisible, setPoVisible] = useState<Record<string, boolean>>({})
  const poVisibleRef = useRef(poVisible)
  const [poHeaderGrid, setPoHeaderGrid] = useState<SheetGrid>(initial?.grid ?? [])
  const poHeaderGridRef = useRef(poHeaderGrid)
  poHeaderGridRef.current = poHeaderGrid
  const [poLocked, setPoLocked] = useState<boolean[]>([])
  const poLockedRef = useRef(poLocked)
  poLockedRef.current = poLocked
  /** Ref để hook chọn ô luôn gọi bản mới nhất của hàm bật/tắt. */
  const togglePoRef = useRef<(col: number) => void>(() => {})
  const [templateId, setTemplateId] = useState('')
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [keepAllTemplateData, setKeepAllTemplateData] = useState(false)
  const [pendingCells, setPendingCells] = useState<Set<string>>(() => initial ? pendingFormulaCells(initial.grid, readRoleRow(initial.grid[0] ?? []).roles) : new Set())

  useEffect(() => {
    if (initial) setPendingCells(pendingFormulaCells(initial.grid, readRoleRow(initial.grid[0] ?? []).roles))
  }, [initial])

  useEffect(() => {
    let cancelled = false
    setInitial(null)
    setLoadError(null)
    getQuoteSheet(supplierId)
      .then((draft) => {
        if (cancelled) return
        const rawGrid = draft?.grid ? normalizeGridInput(draft.grid) : []
        const grid = rawGrid.length > 0 ? rawGrid : starterGrid()
        const pv = normalizePoVisibleInput(draft?.po_visible, grid)
        const headerState = poVisibleByHeader(grid, pv)
        setPoVisible(headerState)
        poVisibleRef.current = headerState
        setPoHeaderGrid(grid)
        poHeaderGridRef.current = grid
        setPoLocked(draft?.po_locked ?? [])
        poLockedRef.current = draft?.po_locked ?? []
        setInitial({ key: Date.now(), grid, sheets: sheetsFromGrid(grid, zoom, pv) })
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Không tải được báo giá dạng bảng')
      })
    return () => {
      cancelled = true
    }
  }, [supplierId])

  const gridWrapRef = useRef<HTMLDivElement | null>(null)
  const eyeRectsRef = useRef(new Map<number, { canvas: HTMLCanvasElement; transform: DOMMatrix; rect: { left: number; top: number; right: number; bottom: number } }>())
  if (typeof window !== 'undefined') (window as unknown as { __poEyeRects?: unknown }).__poEyeRects = eyeRectsRef
  const loaded = initial !== null
  useEffect(() => {
    const el = gridWrapRef.current
    if (!el) return
    let hoveredTarget: HTMLElement | null = null
    const onMouseDown = (event: MouseEvent) => {
      for (const [column, hit] of eyeRectsRef.current) {
        const canvas = hit.canvas
        if (!canvas || !canvas.isConnected) continue
        const canvasRect = canvas.getBoundingClientRect()
        if (
          event.clientX < canvasRect.left ||
          event.clientX > canvasRect.right ||
          event.clientY < canvasRect.top ||
          event.clientY > canvasRect.bottom
        ) {
          continue
        }
        const px = (event.clientX - canvasRect.left) * (canvas.width / canvasRect.width)
        const py = (event.clientY - canvasRect.top) * (canvas.height / canvasRect.height)
        const point = new DOMPoint(px, py).matrixTransform(hit.transform.inverse())
        if (!isPoEyeHit(point.x, point.y, hit.rect)) continue
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        if (editingRef.current) {
          togglePoRef.current(column)
        } else {
          setMessage('Bấm "Sửa" ở thanh trên trước để bật/tắt cột hiện trên PO.')
        }
        return
      }
    }
    const onMouseMove = (event: MouseEvent) => {
      let hitEye = false
      for (const [, hit] of eyeRectsRef.current) {
        const canvas = hit.canvas
        if (!canvas || !canvas.isConnected) continue
        const canvasRect = canvas.getBoundingClientRect()
        if (
          event.clientX < canvasRect.left ||
          event.clientX > canvasRect.right ||
          event.clientY < canvasRect.top ||
          event.clientY > canvasRect.bottom
        ) {
          continue
        }
        const px = (event.clientX - canvasRect.left) * (canvas.width / canvasRect.width)
        const py = (event.clientY - canvasRect.top) * (canvas.height / canvasRect.height)
        const point = new DOMPoint(px, py).matrixTransform(hit.transform.inverse())
        if (isPoEyeHit(point.x, point.y, hit.rect)) {
          hitEye = true
          break
        }
      }
      const target = event.target instanceof HTMLElement ? event.target : null
      if (hitEye) {
        if (target) {
          target.style.cursor = 'pointer'
          hoveredTarget = target
        }
      } else if (hoveredTarget) {
        hoveredTarget.style.cursor = ''
        hoveredTarget = null
      }
    }
    const clearRects = () => eyeRectsRef.current.clear()
    el.addEventListener('mousedown', onMouseDown, true)
    el.addEventListener('mousemove', onMouseMove, { passive: true })
    el.addEventListener('scroll', clearRects, true)
    return () => {
      el.removeEventListener('mousedown', onMouseDown, true)
      el.removeEventListener('mousemove', onMouseMove)
      el.removeEventListener('scroll', clearRects, true)
    }
  }, [loaded])
  useEffect(() => {
    const el = gridWrapRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey || editingRef.current) return
      e.preventDefault()
      setZoom((z) => clampZoom(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1)))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [loaded])

  // Bắt phím Tab / Shift+Tab khi đang sửa ô để chốt giá trị và chuyển sang ô kế bên.
  useEffect(() => {
    const el = gridWrapRef.current
    if (!el) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      if (!editingRef.current) return

      const activeEl = document.activeElement as HTMLElement | null
      const inputBox = el.querySelector<HTMLElement>('.luckysheet-input-box')
      const isCellInputEditing = inputBox ? parseInt(inputBox.style.zIndex || '0', 10) > 0 : false
      const isFxEditing = activeEl?.id === 'luckysheet-functionbox-cell'
      const isEditingCell = isCellInputEditing || isFxEditing

      // Nếu không đang trong trạng thái sửa ô, để FortuneSheet điều hướng mặc định
      if (!isEditingCell) return

      // Nếu đang mở gợi ý danh sách công thức, để Tab chọn công thức
      const formulaSearchActive = Boolean(
        document.querySelector('#luckysheet-formula-search-c .luckysheet-formula-search-item-active')
      )
      if (formulaSearchActive) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      const wb = wbRef.current
      if (!wb) return

      const sel = wb.getSelection()?.[0]
      const r = sel?.row[0] ?? 0
      const c = sel?.column[0] ?? 0
      const totalCols = wb.getAllSheets()[0]?.column ?? 20
      const next = nextCellOnTab({ row: r, col: c }, { totalCols, shiftKey: event.shiftKey })

      const editor = (activeEl && (activeEl.id === 'luckysheet-rich-text-editor' || activeEl.id === 'luckysheet-functionbox-cell'))
        ? activeEl
        : el.querySelector<HTMLElement>('#luckysheet-rich-text-editor')
      if (!editor) return

      // Helper phát sự kiện phím giả lập để FortuneSheet xử lý chuẩn xác
      const dispatchKey = (key: string, code: string, keyCode: number) => {
        editor.dispatchEvent(
          new KeyboardEvent('keydown', {
            key,
            code,
            keyCode,
            which: keyCode,
            bubbles: true,
            cancelable: true,
          })
        )
      }

      // 1. Chốt giá trị ô hiện tại bằng Enter (FortuneSheet chốt giá trị và dời xuống dòng r + 1)
      dispatchKey('Enter', 'Enter', 13)

      // 2. Dời lại lên dòng hiện tại (r)
      dispatchKey('ArrowUp', 'ArrowUp', 38)

      // 3. Dời sang cột tiếp theo theo kết quả hàm điều hướng nextCellOnTab (hoặc giữ nguyên nếu ở mép bảng)
      if (next.col < c) {
        dispatchKey('ArrowLeft', 'ArrowLeft', 37)
      } else if (next.col > c) {
        dispatchKey('ArrowRight', 'ArrowRight', 39)
      }
    }

    el.addEventListener('keydown', onKeyDown, true)
    return () => el.removeEventListener('keydown', onKeyDown, true)
  }, [loaded])

  // Đổi mức zoom: nạp lại lưới với zoomRatio mới (chỉ khi đang xem, không có thay đổi chưa lưu).
  useEffect(() => {
    setInitial((prev) => {
      if (!prev || editingRef.current) return prev
      return { key: Date.now(), grid: prev.grid, sheets: sheetsFromGrid(prev.grid, zoom) }
    })
  }, [zoom])

  // FortuneSheet không tự tính khi nạp; lần đầu có thể còn #VALUE! vì thứ tự phụ thuộc, nên tính hai lượt.
  useEffect(() => {
    if (!initial) return
    const t = setTimeout(() => {
      wbRef.current?.calculateFormula()
      wbRef.current?.calculateFormula()
    }, 0)
    return () => clearTimeout(t)
  }, [initial])

  // FortuneSheet phát onChange cả lúc khởi tạo/tính công thức; chỉ coi là thay đổi khi đang ở chế độ sửa.
  const editingRef = useRef(false)
  useEffect(() => {
    editingRef.current = editing
  }, [editing])
  const handleChange = useCallback(() => {
    if (editingRef.current) {
      setDirty(true)
      const matrix = wbRef.current?.getAllSheets()[0]?.data ?? []
      const rawGrid = trimGrid(gridFromFortuneMatrix(matrix as unknown[][]))
      const normalized = normalizeQuoteSheetUnits(rawGrid)
      const grid = reindexQuoteSheetRows(normalized.grid)
      if (JSON.stringify(grid) !== JSON.stringify(rawGrid)) {
        const maxRows = Math.max(grid.length, rawGrid.length)
        for (let row = 2; row < maxRows; row++) {
          const maxColumns = Math.max(grid[row]?.length ?? 0, rawGrid[row]?.length ?? 0)
          for (let col = 0; col < maxColumns; col++) {
            if (grid[row]?.[col] !== rawGrid[row]?.[col]) wbRef.current?.setCellValue(row, col, grid[row]?.[col] ?? null)
          }
        }
      }
      const currentByHeader = poVisibleByHeader(grid, poVisibleForGrid(grid, poVisibleRef.current, poHeaderGrid))
      poVisibleRef.current = currentByHeader
      setPoVisible(currentByHeader)
      setPoHeaderGrid(grid)
      setPendingCells(pendingFormulaCells(grid, readRoleRow(grid[0] ?? []).roles))
      if (normalized.problems.length) setWarnings(normalized.problems)
    }
  }, [])

  useEffect(() => {
    let alive = true
    materialService
      .list()
      .then((list) => { if (alive) setMaterials(list) })
      .catch(() => { if (alive) setMaterials([]) })
    return () => { alive = false }
  }, [])

  /** Đọc lưới hiện tại từ FortuneSheet và kiểm tra hàng vai trò. */
  const readCurrentGrid = useCallback((): { grid: SheetGrid; problems: string[] } => {
    const wb = wbRef.current
    const matrix = wb?.getAllSheets()[0]?.data ?? []
    const grid = trimGrid(gridFromFortuneMatrix(matrix as unknown[][]))
    const roleRow = grid[0] ?? []
    const headers = grid[1] ?? []
    const { roles, invalid } = readRoleRow(roleRow)
    const problems: string[] = []
    const missingRoleRow = roles.every((r) => r === null) && headers.some((h) => typeof h === 'string' && h.trim() !== '')
    setRoleRowMissing(missingRoleRow)
    if (missingRoleRow) {
      problems.push('Thiếu hàng 1 (hàng Vai trò). Hàng này không nên xóa. Bấm "Thêm lại hàng Vai trò" để khôi phục.')
    } else if (invalid.length > 0) {
      problems.push(
        `Hàng Vai trò có nhãn không hợp lệ ở cột ${invalid.map(columnLetter).join(', ')}. Chọn một trong: ${Object.values(SHEET_ROLE_LABEL).join('; ')}.`,
      )
    }
    const missing = columnsMissingRole(headers, roles)
    if (missing.length > 0) {
      problems.push(`Cột ${missing.map(columnLetter).join(', ')} có tiêu đề nhưng chưa khai báo vai trò.`)
    }
    return { grid, problems }
  }, [])

  /**
   * Áp công thức của ô đang chọn xuống cột (giống kéo điền của Excel). Chọn một ô: áp cho mọi dòng
   * dữ liệu có nội dung. Chọn một vùng nhiều dòng: chỉ áp trong vùng đó. Ô đầu vùng là ô nguồn.
   */
  function fillFormulaDown() {
    const wb = wbRef.current
    if (!wb) return
    const sel = wb.getSelection()?.[0]
    if (!sel) {
      setMessage('Chọn ô có công thức trước, rồi bấm "Áp công thức xuống cột".')
      return
    }
    const [r0, r1] = sel.row
    const col = sel.column[0]
    const grid = gridFromFortuneMatrix((wb.getAllSheets()[0]?.data ?? []) as unknown[][])
    const targets = r1 > r0 ? Array.from({ length: r1 - r0 }, (_, i) => r0 + 1 + i) : undefined
    const plan = planFillFormula(grid, r0, col, targets)
    if (plan.length === 0) {
      setMessage('Ô đang chọn không có công thức, hoặc không có dòng nào bên dưới để áp.')
      return
    }
    wb.batchCallApis(plan.map((p) => ({ name: 'setCellValue', args: [p.r, p.c, p.formula] })))
    // Ô ghi mới không kế thừa font từ celldata: ép Inter để đồng bộ toàn hệ thống.
    for (const p of plan) wb.setCellFormat(p.r, p.c, 'ff', 'Inter')
    wb.calculateFormula()
    wb.calculateFormula()
    setDirty(true)
    setMessage(`Đã áp công thức cho ${plan.length} dòng ở cột ${columnLetter(col)}. Kiểm tra rồi bấm Lưu.`)
  }

  /** Khôi phục hàng Vai trò đã bị xóa: chèn hàng mới ở trên cùng và điền nhãn theo tiêu đề. */
  function restoreRoleRow() {
    const wb = wbRef.current
    if (!wb) return
    wb.insertRowOrColumn('row', 0, 1, 'lefttop')
    const grid = gridFromFortuneMatrix(wb.getAllSheets()[0]?.data ?? []) as SheetGrid
    const headers = grid[1] ?? []
    headers.forEach((h, c) => {
      const role = guessRoleFromLabel(h)
      if (role) {
        wb.setCellValue(0, c, SHEET_ROLE_LABEL[role])
        wb.setCellFormat(0, c, 'ff', 'Inter')
      }
    })
    setRoleRowMissing(false)
    setDirty(true)
    setMessage('Đã thêm lại hàng Vai trò. Kiểm tra vai trò từng cột rồi bấm Lưu.')
  }

  /** Cờ hiện trên PO đủ độ dài theo lưới hiện tại (cột mới mặc định tắt). */
  function poVisibleFor(grid: SheetGrid): boolean[] {
    return poVisibleForGrid(grid, poVisibleRef.current, poHeaderGridRef.current, poLockedRef.current)
  }

  /** Bật/tắt một cột trên PO khi đang sửa. Cột cố định và cột có ô nhập lúc mua không đổi được. */
  function togglePoColumn(col: number) {
    if (!editing) return
    const { grid } = readCurrentGrid()
    const role = readRoleRow(grid[0] ?? []).roles[col] ?? null
    if (role !== null && ['identity', 'compute', 'uom', 'result'].includes(role)) {
      setMessage('Cột này do PO cố định, không bật/tắt được.')
      return
    }
    if (poLockedRef.current[col] || role === 'input') {
      setMessage('Cột có ô nhập lúc mua luôn hiện trên PO, không tắt được.')
      return
    }
    const next = togglePoVisibleColumn(poVisibleFor(grid), col)
    const byHeader = poVisibleByHeader(grid, next)
    poVisibleRef.current = byHeader
    poHeaderGridRef.current = grid
    setPoVisible(byHeader)
    setPoHeaderGrid(grid)
    setDirty(true)
    const on = next[col]
    wbRef.current?.setCellFormat(1, col, 'bg', on ? PO_ON_BG : PO_OFF_BG)
    wbRef.current?.setCellFormat(1, col, 'fc', on ? PO_ON_FC : PO_OFF_FC)
    const headerTitle = String(grid[1]?.[col] ?? '').trim()
    setMessage(on ? `Đã BẬT cột "${headerTitle}" hiện trên PO.` : `Đã TẮT cột "${headerTitle}" khỏi PO.`)
  }
  togglePoRef.current = togglePoColumn

  async function handleSave(applyAfterSave = false) {
    setMessage(null)
    const current = readCurrentGrid()
    const normalized = normalizeQuoteSheetUnits(current.grid)
    const grid = reindexQuoteSheetRows(normalized.grid)
    const problems = [...current.problems, ...normalized.problems]
    setWarnings(problems)
    setSaving(true)
    try {
      const roles = readRoleRow(grid[0] ?? []).roles
      const saved = await saveQuoteSheet(supplierId, { roles, grid, po_visible: poVisibleFor(grid) })
      const savedByHeader = poVisibleByHeader(saved.grid, saved.po_visible ?? [])
      poVisibleRef.current = savedByHeader
      setPoVisible(savedByHeader)
      poHeaderGridRef.current = saved.grid
      setPoHeaderGrid(saved.grid)
      setDirty(false)
      setEditing(false)
      setMessage(
        problems.length > 0
          ? 'Đã lưu nháp. Còn cột chưa khai báo vai trò, chưa thể phát hành.'
          : 'Đã lưu nháp báo giá.',
      )
      if (applyAfterSave && problems.length === 0) await applySavedSheet(grid, roles)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không lưu được báo giá')
    } finally {
      setSaving(false)
    }
  }

  /** Áp dụng bảng đang hiển thị (đã lưu) mà không cần sửa. */
  async function handleApplyCurrent() {
    setMessage(null)
    const current = readCurrentGrid()
    const normalized = normalizeQuoteSheetUnits(current.grid)
    const grid = reindexQuoteSheetRows(normalized.grid)
    const problems = [...current.problems, ...normalized.problems]
    setWarnings(problems)
    if (problems.length > 0) {
      setMessage('Còn cột chưa khai báo vai trò, chưa thể áp dụng.')
      return
    }
    await applySavedSheet(grid, readRoleRow(grid[0] ?? []).roles)
  }

  /** Tên hàng trong bảng chưa có vật tư: tạo vật tư theo tên (dùng lại vật tư cùng tên nếu có) để PO chọn được. */
  async function linkMissingMaterials(pv: PublishPreview, ids: Record<string, string | null>): Promise<Record<string, string | null>> {
    const groups = new Map<string, { name: string; uom: string; rows: number[] }>()
    for (const line of pv.lines) {
      if (line.item_linked) continue
      const key = normalizeItemName(line.supplier_item_name)
      if (!key) continue
      const g = groups.get(key) ?? { name: line.supplier_item_name, uom: line.uom || 'Pcs', rows: [] }
      g.rows.push(line.row)
      groups.set(key, g)
    }
    if (groups.size === 0) return ids
    const out = { ...ids }
    const created: Material[] = []
    for (const [key, g] of groups) {
      const existing = materials.find((m) => normalizeItemName(m.name) === key)
      const material = existing ?? await materialService.create({
        code: '',
        name: g.name.trim(),
        kind: 'RAW',
        status: 'active',
        uom_default: normalizeUomLabel(g.uom),
      })
      if (!existing) created.push(material)
      for (const row of g.rows) out[String(row)] = material.id
    }
    if (created.length) setMaterials((prev) => [...prev, ...created])
    return out
  }

  /** Lưu & áp dụng: xem trước rồi áp dụng ngay nếu không còn lỗi chặn; hàng chưa có vật tư thì tự tạo vật tư; có lỗi thì giữ bản xem trước để sửa. */
  async function applySavedSheet(grid: SheetGrid, roles: ReturnType<typeof readRoleRow>["roles"]) {
    setPublishing(true)
    try {
      let ids: Record<string, string | null> = { ...suggestItemIdsByName(grid, roles, materials), ...itemIds }
      const makeBody = () => ({ roles, grid, uom_default: uomDefault, item_ids: ids, po_visible: poVisibleFor(grid) })
      let pv = await previewQuoteSheetPublish(supplierId, makeBody())
      if (pv.unlinked_count > 0) {
        ids = await linkMissingMaterials(pv, ids)
        pv = await previewQuoteSheetPublish(supplierId, makeBody())
      }
      setItemIds(ids)
      setPreview(pv)
      if (!pv.can_publish) {
        setMessage('Đã lưu nhưng chưa áp dụng được: còn lỗi trong bản xem trước bên dưới. Sửa rồi bấm "Lưu" lại.')
        return
      }
      const key = `quote-sheet-${supplierId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const result = await publishQuoteSheet(supplierId, makeBody(), key)
      setPreview(null)
      setMessage(`Đã lưu và áp dụng báo giá${result.quote_id ? ` (mã ${result.quote_id})` : ''}. Tạo PO và BOM dùng được ngay; báo giá cũ đã hết hiệu lực.`)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không áp dụng được báo giá')
    } finally {
      setPublishing(false)
    }
  }

  async function handleCalculate() {
    setMessage(null)
      const current = readCurrentGrid()
      const normalized = normalizeQuoteSheetUnits(current.grid)
      const grid = reindexQuoteSheetRows(normalized.grid)
      const problems = [...current.problems, ...normalized.problems]
    setWarnings(problems)
    try {
      const result = await calculateQuoteSheet(supplierId, grid, readRoleRow(grid[0] ?? []).roles)
      const wb = wbRef.current
      const pending = new Set(result.pendingRows)
      const mismatches = Object.entries(result.values)
        .filter(([address, serverValue]) => {
          const pos = parseAddress(address)
          if (!pos || !wb) return false
          // Dòng chờ nhập lúc mua: không có kết quả để đối chiếu.
          if (pending.has(pos.row + 1)) return false
          return !sameValue(serverValue, wb.getCellValue(pos.row, pos.col, { type: 'v' }))
        })
        .map(([address]) => address)
      setCalc({ ...result, mismatches })
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không tính lại được báo giá')
    }
  }

  /** Xem trước với vật tư đã chọn; dòng chưa chọn thì gợi ý theo tên (chỉ khi đúng một vật tư khớp). */
  async function runPreview(choices: Record<string, string | null>) {
    setMessage(null)
      const current = readCurrentGrid()
      const normalized = normalizeQuoteSheetUnits(current.grid)
      const grid = reindexQuoteSheetRows(normalized.grid)
      const problems = [...current.problems, ...normalized.problems]
    setWarnings(problems)
    try {
      const roles = readRoleRow(grid[0] ?? []).roles
      const merged = { ...suggestItemIdsByName(grid, roles, materials), ...choices }
      setItemIds(merged)
      setPreview(await previewQuoteSheetPublish(supplierId, { roles, grid, uom_default: uomDefault, item_ids: merged, po_visible: poVisibleFor(grid) }))
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không xem trước được phát hành')
    }
  }

  async function handlePreviewPublish() {
    await runPreview(itemIds)
  }

  /** Chọn hoặc bỏ vật tư của một dòng trong bảng xem trước, rồi xem trước lại. */
  async function handlePickItem(rowNumber: number, material: Material | null) {
    const next = { ...itemIds }
    if (material) next[String(rowNumber)] = material.id
    else next[String(rowNumber)] = null
    setItemIds(next)
    await runPreview(next)
  }

  /** Danh sách mẫu dùng lại. */
  const loadTemplates = useCallback(() => {
    listQuoteSheetTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]))
  }, [])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  /** Lưu lưới đang mở thành mẫu có tên. */
  async function handleSaveTemplate() {
    const name = templateName.trim()
    if (!name) return
    setMessage(null)
    try {
      const { grid } = readCurrentGrid()
      const savedGrid = templateGrid(grid, keepAllTemplateData)
      const savedRoles = readRoleRow(savedGrid[0] ?? []).roles
      const templateVisible = poVisibleForGrid(savedGrid, poVisibleRef.current, poHeaderGrid, poLocked)
      const saved = await saveQuoteSheetTemplate(name, { roles: savedRoles, grid: savedGrid, po_visible: templateVisible })
      setTemplateId(saved.id)
      setTemplateDialogOpen(false)
      setTemplateName('')
      setKeepAllTemplateData(false)
      loadTemplates()
      setMessage(`Đã lưu mẫu "${saved.name}".`)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không lưu được mẫu')
    }
  }

  async function handleDeleteTemplate(tpl: QuoteSheetTemplate) {
    if (!window.confirm(`Xóa mẫu “${tpl.name}”?`)) return
    try {
      await deleteQuoteSheetTemplate(tpl.id)
      if (templateId === tpl.id) setTemplateId('')
      loadTemplates()
      setMessage(`Đã xóa mẫu “${tpl.name}”.`)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không xóa được mẫu')
    }
  }

  /** Áp mẫu đã chọn vào NCC này: thay bản nháp hiện tại (có xác nhận nếu đang có thay đổi chưa lưu). */
  async function handleApplyTemplate() {
    if (!templateId) return
    if (dirty && !window.confirm('Áp mẫu sẽ thay bảng hiện tại và mất các thay đổi chưa lưu. Tiếp tục?')) return
    setMessage(null)
    try {
      await applyQuoteSheetTemplate(supplierId, templateId)
      const draft = await getQuoteSheet(supplierId)
      const rawGrid = draft?.grid ? normalizeGridInput(draft.grid) : []
      const grid = rawGrid.length > 0 ? rawGrid : starterGrid()
      const pv = normalizePoVisibleInput(draft?.po_visible, grid)
      const byHeader = poVisibleByHeader(grid, pv)
      setPoVisible(byHeader)
      poVisibleRef.current = byHeader
      setPoHeaderGrid(grid)
      setPoLocked(draft?.po_locked ?? [])
      setDirty(false)
      setInitial({ key: Date.now(), grid, sheets: sheetsFromGrid(grid, zoom, pv) })
      setMessage('Đã áp mẫu vào báo giá của NCC này.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không áp được mẫu')
    }
  }

  /** Thoát chế độ sửa: có thay đổi chưa lưu thì phải xác nhận; rồi bỏ thay đổi và nạp lại bản đã lưu. */
  async function exitEditing() {
    if (dirty && !window.confirm('Bạn đang sửa báo giá và có thay đổi chưa lưu. Thoát sẽ mất các thay đổi này?')) return
    setEditing(false)
    setDirty(false)
    setMessage(null)
    try {
      const draft = await getQuoteSheet(supplierId)
      const rawGrid = draft?.grid ? normalizeGridInput(draft.grid) : []
      const grid = rawGrid.length > 0 ? rawGrid : starterGrid()
      setInitial({ key: Date.now(), grid, sheets: sheetsFromGrid(grid, zoom) })
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Không tải lại được báo giá đã lưu')
    }
  }

  // Cảnh báo khi đóng/tải lại trang lúc đang sửa có thay đổi chưa lưu.
  useEffect(() => {
    if (!(editing && dirty)) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [editing, dirty])

  if (loadError) return <p className="text-sm text-red-700">{loadError}</p>
  if (!initial) return <p className="text-sm text-slate-600">Đang tải báo giá dạng bảng…</p>

  const roleInfo = readRoleRow(initial.grid[0] ?? []).roles
  const unlistedHeaders = unlistedPoColumnHints(initial.grid, roleInfo)

  return (
    <section className="quote-sheet-panel space-y-3" aria-label="Báo giá dạng bảng tính">
        <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">Báo giá dạng bảng tính</h3>
        {editing ? (
          <>
            <Button variant="primary" onClick={() => handleSave(true)} disabled={saving || publishing}>
              {saving || publishing ? 'Đang lưu…' : 'Lưu'}
            </Button>
            <Button variant="secondary" onClick={exitEditing} disabled={saving}>
              Thoát sửa
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={() => { setEditing(true); setMessage(null) }}>
              Sửa
            </Button>
            <Button variant="secondary" onClick={handleApplyCurrent} disabled={publishing}>
              {publishing ? 'Đang áp dụng…' : 'Áp dụng báo giá này'}
            </Button>
          </>
        )}
        {editing ? (
          <>
            <Button variant="secondary" onClick={() => setTemplateDialogOpen(true)}>
              Lưu làm mẫu
            </Button>
            <select
              className="rounded border px-2 py-1 text-sm"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              aria-label="Chọn mẫu báo giá"
            >
              <option value="">— Chọn mẫu —</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
              ))}
            </select>
            <Button variant="secondary" onClick={handleApplyTemplate} disabled={!templateId}>
              Áp mẫu
            </Button>
            <Button variant="secondary" onClick={fillFormulaDown} title="Chọn ô có công thức (hoặc một vùng nhiều dòng) rồi bấm để áp công thức xuống cột">
              Áp công thức xuống cột
            </Button>
          </>
        ) : null}
        {editing && roleRowMissing ? (
          <Button variant="secondary" onClick={restoreRoleRow}>
            Thêm lại hàng Vai trò
          </Button>
        ) : null}
        <Button variant="secondary" onClick={handleCalculate}>
          Tính lại và đối chiếu
        </Button>
        <label className="text-sm text-slate-700">
          Đơn vị mặc định{' '}
          <input
            className="rounded border px-2 py-1"
            value={uomDefault}
            onChange={(e) => setUomDefault(e.target.value)}
            aria-label="Đơn vị mặc định"
          />
        </label>
        <Button variant="secondary" onClick={handlePreviewPublish}>
          Xem trước phát hành
        </Button>
        <span className="text-sm text-slate-600">{dirty ? 'Có thay đổi chưa lưu' : ''}</span>
      </div>

      <p className="text-sm text-slate-600">
        Hàng 1 là vai trò của từng cột (Tên hàng, Quy cách / màu, Đơn giá, Nhập lúc mua, Chỉ mô tả). Hàng 2 là tiêu đề.
        Dữ liệu từ hàng 3. Công thức viết như Excel, ví dụ <code>=F3*H3</code>.
      </p>
      <p className="text-xs text-slate-600">Đơn vị chuẩn: {QUOTE_SHEET_UOM_OPTIONS.join(' · ')}. Có thể gõ m2/m3; bảng tự đổi thành m²/m³ khi xác nhận ô.</p>
      {unlistedHeaders.map((header) => <p key={header} className="text-xs text-slate-600">Cột “{header}” đang là Chỉ mô tả — muốn dùng để lọc khi lên PO thì đặt hàng 1 là “Quy cách / màu”.</p>)}

      {message ? <p className="text-sm text-slate-800" role="status">{message}</p> : null}
      {warnings.map((w) => (
        <p key={w} className="text-sm text-red-700">{w}</p>
      ))}

      <EditNavigationGuard active={editing && dirty} />
      <div ref={gridWrapRef} className="quote-sheet-panel__grid" style={{ height: 'calc(100vh - 360px)', minHeight: 360 }}>
        <Workbook
          key={initial.key}
          ref={wbRef}
          data={initial.sheets}
          lang="en"
          allowEdit={editing}
          showToolbar
          showFormulaBar={editing}
          onChange={handleChange}
          hooks={{
            beforeRenderCell: (_cell, info, ctx) => {
              if (!shouldMaskPendingFormulaCell(pendingCells, info.row, info.column)) return true
              ctx.clearRect(info.startX, info.startY, info.endX - info.startX, info.endY - info.startY)
              ctx.fillStyle = '#ffffff'
              ctx.fillRect(info.startX, info.startY, info.endX - info.startX, info.endY - info.startY)
              ctx.strokeStyle = '#d7dee8'
              ctx.strokeRect(info.startX, info.startY, info.endX - info.startX, info.endY - info.startY)
              return false
            },
            afterRenderCell: (cell, info, ctx) => {
              if (info.row !== 1) return
              const grid = poHeaderGridRef.current
              const roles = readRoleRow(grid[0] ?? []).roles
              const locked = poLockedRef.current
              const state = poEyeStateForColumn(
                grid[1]?.[info.column],
                roles[info.column] ?? null,
                poVisibleForGrid(grid, poVisibleRef.current, grid, locked)[info.column] === true,
                locked[info.column] === true,
              )
              if (state === 'none') {
                eyeRectsRef.current.delete(info.column)
                return
              }
              const size = 12
              const centerX = info.endX - 12
              const centerY = (info.startY + info.endY) / 2
              const hit = { left: centerX - 8, top: centerY - 8, right: centerX + 8, bottom: centerY + 8 }
              const canvas = ctx.canvas
              eyeRectsRef.current.set(info.column, { canvas, transform: new DOMMatrix(Array.from(ctx.getTransform().toFloat64Array())), rect: hit })
              ctx.save()
              if (state === 'on') {
                // Đang BẬT hiện trên PO: nền nhẹ và viền xanh sky, đồng tử rõ ràng
                ctx.fillStyle = '#e0f2fe'
                if (typeof ctx.roundRect === 'function') {
                  ctx.beginPath()
                  ctx.roundRect(centerX - 8, centerY - 8, 16, 16, 3)
                  ctx.fill()
                }
                ctx.strokeStyle = '#0284c7'
                ctx.fillStyle = '#0284c7'
                ctx.lineWidth = 1.3
                ctx.beginPath()
                ctx.moveTo(centerX - size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY - 4.5, centerX + size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY + 4.5, centerX - size / 2, centerY)
                ctx.stroke()
                ctx.beginPath()
                ctx.arc(centerX, centerY, 1.8, 0, Math.PI * 2)
                ctx.fill()
              } else if (state === 'always') {
                // Cột cố định luôn hiện trên PO (có ô nhập lúc mua)
                ctx.fillStyle = '#f1f5f9'
                if (typeof ctx.roundRect === 'function') {
                  ctx.beginPath()
                  ctx.roundRect(centerX - 8, centerY - 8, 16, 16, 3)
                  ctx.fill()
                }
                ctx.strokeStyle = '#475569'
                ctx.fillStyle = '#475569'
                ctx.lineWidth = 1.3
                ctx.beginPath()
                ctx.moveTo(centerX - size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY - 4.5, centerX + size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY + 4.5, centerX - size / 2, centerY)
                ctx.stroke()
                ctx.beginPath()
                ctx.arc(centerX, centerY, 1.8, 0, Math.PI * 2)
                ctx.fill()
              } else {
                // Đang TẮT (state === 'off'): viền xám mờ và gạch chéo đỏ rõ ràng (EyeOff)
                ctx.strokeStyle = '#94a3b8'
                ctx.lineWidth = 1.1
                ctx.beginPath()
                ctx.moveTo(centerX - size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY - 4, centerX + size / 2, centerY)
                ctx.quadraticCurveTo(centerX, centerY + 4, centerX - size / 2, centerY)
                ctx.stroke()

                // Gạch chéo biểu tượng tắt rõ ràng
                ctx.strokeStyle = '#ef4444'
                ctx.lineWidth = 1.4
                ctx.beginPath()
                ctx.moveTo(centerX - 5.5, centerY - 5.5)
                ctx.lineTo(centerX + 5.5, centerY + 5.5)
                ctx.stroke()
              }
              ctx.restore()
              void cell
            },
            afterUpdateCell: (row, column, _oldValue, newValue) => {
              if (!(newValue && typeof newValue === 'object' && (newValue as { ff?: unknown }).ff === 'Inter')) {
                wbRef.current?.setCellFormat(row, column, 'ff', 'Inter')
              }
              const current = trimGrid(gridFromFortuneMatrix((wbRef.current?.getAllSheets()[0]?.data ?? []) as unknown[][]))
              const roles = readRoleRow(current[0] ?? []).roles
              const header = String(current[1]?.[column] ?? '').trim().toLocaleLowerCase('vi')
              if (roles[column] === 'uom' || header === 'đv đơn giá') {
                // afterUpdateCell có thể nhận object metadata khi FortuneSheet xử lý dropdown.
                // Đọc giá trị ô từ ma trận hiện tại trước, rồi mới rơi về tham số hook.
                const raw = quoteSheetCellText(current[row]?.[column] ?? newValue)
                const normalized = normalizeQuoteSheetUnit(raw)
                if (!normalized.error && normalized.value !== raw) wbRef.current?.setCellValue(row, column, normalized.value)
                if (raw && normalized.error) setMessage(`Dòng ${row + 1}: đơn vị '${raw.trim()}' không có trong danh sách.`)
              }
              const indexed = reindexQuoteSheetRows(current)
              for (let r = 2; r < indexed.length; r++) {
                for (let c = 0; c < (indexed[r]?.length ?? 0); c++) {
                  if (indexed[r]?.[c] !== current[r]?.[c]) wbRef.current?.setCellValue(r, c, indexed[r]?.[c] ?? null)
                }
              }
            },
          }}
        />
      </div>

      {preview ? (
        <div className="space-y-1 text-sm" data-testid="quote-sheet-preview">
          <p>
            Sẽ tạo {preview.line_count} dòng giá: {preview.new_count} dòng mới, {preview.line_count - preview.new_count} dòng đã có giá cũ.
            Giữ vật tư cũ: {preview.kept_count} dòng · Chưa gắn: {preview.unlinked_count} dòng.
            {preview.replaces_active_quotes ? ` Lưu sẽ áp dụng ngay và thay thế ${preview.replaces_active_quotes} báo giá đang áp dụng của NCC này.` : ' Lưu sẽ áp dụng ngay.'}
          </p>
          {preview.blocking.map((b) => (
            <p key={b} className="text-red-700">{b}</p>
          ))}
          {preview.row_errors.map((e) => (
            <p key={`${e.row}-${e.message}`} className="text-red-700">Dòng {e.row}: {e.message}</p>
          ))}
          {preview.lines.length > 0 ? (
            <table className="mt-1 w-full border-collapse text-left">
              <thead>
                <tr className="border-b">
                  <th className="px-2 py-1">STT</th>
                  <th className="px-2 py-1">Tên hàng</th>
                  <th className="px-2 py-1">ĐVT</th>
                  <th className="px-2 py-1 text-right">Đơn giá mới</th>
                  <th className="px-2 py-1 text-right">Giá cũ</th>
                  <th className="px-2 py-1 text-right">Chênh lệch</th>
                  <th className="px-2 py-1">Máy hiểu</th>
                  <th className="px-2 py-1">Vật tư liên kết</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr key={l.row_ref} className="border-b">
                    <td className="px-2 py-1">{i + 1}</td>
                    <td className="px-2 py-1">{l.supplier_item_name}</td>
                    <td className="px-2 py-1">{l.uom}</td>
                    <td className="px-2 py-1 text-right">{formatSheetAmount(l.unit_price)}</td>
                    <td className="px-2 py-1 text-right">{l.old_unit_price == null ? 'Mới' : formatSheetAmount(l.old_unit_price)}</td>
                    <td className="px-2 py-1 text-right">{l.diff == null ? '—' : formatSheetAmount(l.diff)}</td>
                    <td className="px-2 py-1 text-xs" data-testid={`preview-classify-${l.row}`}>
                      {describeRowClassification(l.mappings, l.input_names)}
                    </td>
                    <td className="px-2 py-1 min-w-56">
                      {l.item_source === 'kept_from_active' ? <span className="mr-1 text-xs text-slate-500">giữ từ báo giá cũ</span> : null}
                      <Typeahead<Material>
                        items={materials}
                        value={materials.find((m) => m.id === l.item_id) ?? null}
                        onChange={(m) => void handlePickItem(l.row, m)}
                        displayValue={(m) => `${m.code} · ${m.name}`}
                        placeholder="Chọn vật tư"
                        getItemKey={(m) => m.id}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}

      {calc ? (
        <div className="space-y-1 text-sm" data-testid="quote-sheet-calc">
          <p>
            Máy chủ tính {calc.formulaCount} ô công thức. Lỗi: {calc.errors.length}. Lệch so với lưới: {calc.mismatches.length}. Chờ nhập lúc mua: {calc.pendingRows.length} dòng.
          </p>
          {calc.errors.map((e) => (
            <p key={e.cell} className="text-red-700">Ô {e.cell}: {e.message}</p>
          ))}
          {calc.mismatches.length > 0 ? (
            <p className="text-red-700">Các ô lệch: {calc.mismatches.join(', ')}</p>
          ) : null}
        </div>
      ) : null}

      {templateDialogOpen ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/40 p-4" role="presentation" onKeyDown={(event) => {
          if (event.key === 'Escape') setTemplateDialogOpen(false)
        }}>
          <section className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="quote-template-title">
            <h4 id="quote-template-title" className="mb-3 text-lg font-semibold">Lưu mẫu báo giá</h4>
            <label className="mb-3 block text-sm font-medium">Tên mẫu
              <input className="mt-1 block w-full rounded border border-slate-300 px-3 py-2" value={templateName} onChange={(event) => setTemplateName(event.target.value)} autoFocus />
            </label>
            <label className="mb-2 flex items-start gap-2 text-sm">
              <input type="radio" checked={!keepAllTemplateData} onChange={() => setKeepAllTemplateData(false)} />
              <span>Chỉ cột, công thức và 3 dòng đầu làm ví dụ</span>
            </label>
            <label className="mb-4 flex items-start gap-2 text-sm">
              <input type="radio" checked={keepAllTemplateData} onChange={() => setKeepAllTemplateData(true)} />
              <span>Giữ toàn bộ dữ liệu</span>
            </label>
            <div className="mb-4 max-h-56 overflow-auto rounded border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b bg-slate-50"><th className="w-16 px-2 py-1">STT</th><th className="px-2 py-1">Mẫu đã lưu</th><th className="w-24 px-2 py-1">Thao tác</th></tr></thead>
                <tbody>{templates.map((tpl, index) => <tr key={tpl.id} className="border-b last:border-0"><td className="px-2 py-1">{index + 1}</td><td className="px-2 py-1">{tpl.name}</td><td className="px-2 py-1"><Button variant="danger" size="sm" onClick={() => void handleDeleteTemplate(tpl)}>Xóa</Button></td></tr>)}</tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTemplateDialogOpen(false)}>Hủy</Button>
              <Button onClick={() => void handleSaveTemplate()} disabled={!templateName.trim()}>Lưu mẫu</Button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  )
}
